import { v4 as uuidv4 } from 'uuid';
import { reactive } from 'vue';
import { DossierId } from '@/dossier/models/AbstractDossier';
import MediaData from '@/media/models/MediaData';
import MediaFileType, { parseMediaFileType } from '@/shared/constants/MediaFileType';
import { isPresent, present } from '@/shared/utils/typescript';
import MediaFileClient2, { DiagnosticMediaData, DossierMediaData, JobMediaData, SASData, WheelMediaData } from '../client/MediaFileClient2';
import EntityType from '../constants/EntityType';
import { parseMediaFile2 } from '../converters/mediaFileConverters2';
import MediaContext2 from '../models/MediaContext2';
import MediaFile2 from '../models/MediaFile2';
import pushService from './PushService';
import MediaTargetData from '@/general/models/MediaTargetData';
import { JobId } from '@/jobs/models/AbstractJob';
import SubEntityType from '@/general/constants/SubEntityType';
import { DiagnosticId } from '@/jobs/models/diagnosis/Diagnostic';
import serviceEvents, { MediaUpdated } from '@/shared/services/events/serviceEvents';
import EditMediaData from '@/media/models/EditMediaData';

interface RegisterData {
  relativePath: string;
  type: MediaFileType;
  isLocked: boolean;
  isWarranty: boolean;
}

class MediaFileService2 {
  async loadForDossier(dossierId: DossierId): Promise<MediaContext2> {
    const files = await MediaFileClient2.loadAllForDossier(dossierId);
    const context = reactive(new MediaContext2(dossierId, files)) as MediaContext2;
    const channel = `dossiers-${context.dossierId}/media-processed`;
    this.startListUpdateListener(context, channel);
    return context;
  }

  async loadForFiles(files: Array<MediaFile2>, dossierId: DossierId, channel: string): Promise<MediaContext2> {
    const context = reactive(new MediaContext2(dossierId, files)) as MediaContext2;
    this.startListUpdateListener(context, channel);
    return context;
  }

  private startListUpdateListener(context: MediaContext2, channel: string): void {
    context.freeHooks.push(pushService.bind(channel, (event) => {
      const file = present(parseMediaFile2(event));
      context.updateList(file);
    }));
  }

  async edit(mediaFiles: Array<MediaFile2>, locked?: boolean, warranty?: boolean): Promise<void> {
    await MediaFileClient2.edit(mediaFiles, locked, warranty);
    mediaFiles.forEach(mediaFile => mediaFile.isLocked = isPresent(locked) ? locked : mediaFile.isLocked);
    mediaFiles.forEach(mediaFile => mediaFile.isWarranty = isPresent(warranty) ? warranty : mediaFile.isWarranty);
  }

  async remove(mediaFiles: Array<MediaFile2>, context: MediaContext2, targetData: MediaTargetData): Promise<void> {
    await MediaFileClient2.remove(mediaFiles);
    mediaFiles.forEach(file => context.removeFile(present(file.id)));
    serviceEvents.fire(new MediaUpdated(context.getFilteredList(targetData).length, targetData)).then();
  }

  async updateNote(file: MediaFile2, note: string): Promise<void> {
    await MediaFileClient2.updateNote(file, note);
    file.note = note;
  }

  async create(context: MediaContext2, data: MediaData, targetData: MediaTargetData): Promise<void> {
    const dossierId = context.dossierId;
    const fileNames = data.files.map(f => `${uuidv4()}.${f.name.split('.').pop()}`);
    const sasDataList = await MediaFileClient2.getSASData(dossierId, targetData, fileNames);

    context.updateList(...sasDataList.map(sasData => {
      return MediaFile2.placeholder(sasData.blobUri, data, targetData);
    }));

    serviceEvents.fire(new MediaUpdated(context.getFilteredList(targetData).length, targetData)).then();
    this.upload(dossierId, sasDataList, data, targetData).then();
  }

  async createEditedImage(context: MediaContext2, data: EditMediaData): Promise<void> {
    const dossierId = context.dossierId;
    const fileName = `${uuidv4()}.jpeg`;
    const sasDataList = await MediaFileClient2.getSASData(dossierId, data.target, [fileName]);

    context.updateList(...sasDataList.map(sasData => {
      return MediaFile2.placeholder(sasData.blobUri, data, data.target);
    }));

    serviceEvents.fire(new MediaUpdated(context.getFilteredList(data.target).length, data.target)).then();
    this.uploadEdited(dossierId, sasDataList, data, data.target).then();
  }

  private getRegisterData(sasData: SASData, data: MediaData | EditMediaData, type: MediaFileType): RegisterData {
    return {
      relativePath: sasData.blobRelativePath,
      type,
      isLocked: !data.sharedWithClient,
      isWarranty: data.isWarranty,
    };
  }

  private async uploadEdited(dossierId: DossierId, sasDataList: Array<SASData>, data: EditMediaData, targetData: MediaTargetData): Promise<void> {
    const uploadStatuses = await Promise.allSettled(sasDataList.map((sasData) => {
      return MediaFileClient2.uploadBase64ToAzure(sasData, data.file, data.description);
    }));

    const registerData = sasDataList.filter((_, index) => uploadStatuses[index].status === 'fulfilled')
      .map(sasData => this.getRegisterData(sasData, data, parseMediaFileType('image')));

    this.register(targetData, registerData, data, dossierId);
  }

  private async upload(dossierId: DossierId, sasDataList: Array<SASData>, data: MediaData, targetData: MediaTargetData): Promise<void> {
    const uploadStatuses = await Promise.allSettled(sasDataList.map((sasData, index) => {
      return MediaFileClient2.uploadToAzure(sasData, data.files[index]);
    }));

    const uploadedFiles = data.files.filter((_, index) => uploadStatuses[index].status === 'fulfilled');

    const registerData = uploadedFiles.map((file, index) => {
      return this.getRegisterData(sasDataList[index], data, parseMediaFileType(file.type));
    });

    this.register(targetData, registerData, data, dossierId);
  }

  private async register(targetData: MediaTargetData, fileData: Array<RegisterData>, data: MediaData | EditMediaData, dossierId: DossierId): void {
    switch (targetData.entityType) {
      case EntityType.DOSSIER:
        fileData.forEach((d: { [key: string]: any }) => {
          d.description = data.description;
          d.date = data.date;
        });
        await MediaFileClient2.registerDossierMedia(dossierId, fileData as Array<DossierMediaData>);
        break;

      case EntityType.WHEELS:
        fileData.forEach((d: { [key: string]: any }) => {
          d.subEntity = targetData.subEntityType;
          d.subEntityId = targetData.subEntityType === SubEntityType.TIRE ? targetData.subEntityId : targetData.entityId;
        });
        await MediaFileClient2.registerWheelMedia(dossierId, fileData as Array<WheelMediaData>);
        break;

      case EntityType.JOB:
        if (targetData.subEntityType === SubEntityType.DIAGNOSTIC) {
          await MediaFileClient2.registerDiagnosticMedia(dossierId, targetData.entityId as JobId, targetData.subEntityId as DiagnosticId, fileData as Array<DiagnosticMediaData>);
        } else {
          await MediaFileClient2.registerJobMedia(dossierId, targetData.entityId as JobId, fileData as Array<JobMediaData>);
        }
        break;

      case EntityType.DRIVE_BOOKING:
        await MediaFileClient2.registerDriveBookingMedia(present(targetData.entityId), fileData);
        break;
    }
  }
}

export default new MediaFileService2();